import { useEffect, useRef, useState } from "react";
import { ArrowCounterClockwise, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inputCls, labelCls, btnPrimary, ReadOnlyNote } from "./shared";

const CATS = [["prenotazioni", "Prenotazioni"], ["ordini", "Ordini"], ["servizi", "Servizi"], ["marketing", "Marketing"]];
const CHANNELS = [["email", "Email"], ["whatsapp", "WhatsApp"], ["sms", "SMS"]];
const EMPTY_FORM = {
  subject: "", body: "", wa_body: "", sms_body: "",
  titolo: "", sottotitolo: "", cta_label: "",
  channels: { email: true, whatsapp: true, sms: false },
};

const formFrom = (t) => ({
  subject: t.subject, body: t.body,
  wa_body: t.wa_body || "", sms_body: t.sms_body || "",
  titolo: t.titolo || "", sottotitolo: t.sottotitolo || "", cta_label: t.cta_label || "",
  channels: { email: true, whatsapp: true, sms: false, ...(t.channels || {}) },
});

export default function TestiNotificheSection({ readOnly }) {
  const [templates, setTemplates] = useState(null);
  const [cat, setCat] = useState("prenotazioni");
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [chTab, setChTab] = useState("email");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // {html, body, wa, sms}
  const [previewLoading, setPreviewLoading] = useState(false);
  const textareaRef = useRef(null); // textarea del tab attivo (una sola visibile alla volta)
  const [waApproved, setWaApproved] = useState([]);   // template Meta APPROVED agganciabili
  const [waTplSel, setWaTplSel] = useState("");       // template agganciato manualmente ("" = automatico)
  const waTplInit = useRef("");

  useEffect(() => {
    if (chTab === "whatsapp")
      api.get("/integrations/whatsapp/templates/approved").then((r) => setWaApproved(r.data || [])).catch(() => {});
  }, [chTab]);

  // cambio template agganciato -> anteprima aggiornata col corpo reale del template
  useEffect(() => { if (sel) refreshPreview(sel, form); }, [waTplSel]);

  const load = () => api.get("/settings/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const current = templates?.find((t) => t.key === sel);

  const refreshPreview = async (key, f) => {
    setPreviewLoading(true);
    try {
      const r = await api.post("/settings/templates/preview", {
        key, subject: f.subject, body: f.body, wa_body: f.wa_body, sms_body: f.sms_body,
        titolo: f.titolo, sottotitolo: f.sottotitolo, cta_label: f.cta_label,
        wa_template: waTplSel,
      });
      setPreview(r.data);
    } catch { setPreview(null); }
    finally { setPreviewLoading(false); }
  };

  const open = (t) => {
    setSel(t.key);
    setChTab("email");
    const f = formFrom(t);
    setForm(f);
    const manual = t.wa_meta?.manual ? (t.wa_meta.name || "") : "";
    setWaTplSel(manual);
    waTplInit.current = manual;
    refreshPreview(t.key, f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (waTplSel !== waTplInit.current) payload.wa_template = waTplSel;  // cambio aggancio template Meta
      await api.put(`/settings/templates/${sel}`, payload);
      waTplInit.current = waTplSel;
      toast.success("Template salvato");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      await api.post(`/settings/templates/${sel}/reset`);
      const r = await api.get("/settings/templates");
      setTemplates(r.data);
      const t = r.data.find((x) => x.key === sel);
      if (t) {
        const f = formFrom(t);
        setForm(f);
        refreshPreview(sel, f);
      }
      toast.success("Template ripristinato al predefinito");
    } catch (err) { toast.error(apiError(err)); }
  };

  const insertVar = (v) => {
    const field = chTab === "email" ? "body" : chTab === "whatsapp" ? "wa_body" : "sms_body";
    const tag = `{{${v}}}`;
    const el = textareaRef.current;
    const cur = form[field] || "";
    if (el && typeof el.selectionStart === "number") {
      // inserisce alla posizione del cursore (non in fondo)
      const start = el.selectionStart;
      const end = el.selectionEnd ?? start;
      setForm({ ...form, [field]: cur.slice(0, start) + tag + cur.slice(end) });
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + tag.length, start + tag.length);
      });
    } else {
      setForm({ ...form, [field]: cur + (cur && !cur.endsWith(" ") ? " " : "") + tag });
    }
  };

  const toggleCh = (ch) => {
    if (readOnly) return;
    setForm({ ...form, channels: { ...form.channels, [ch]: !form.channels[ch] } });
  };

  if (!templates) return <div className="text-sm text-zinc-400 py-8 text-center" data-testid="tpl-loading">Caricamento...</div>;

  return (
    <div className="space-y-5" data-testid="setsection-testi">
      <p className="text-xs text-zinc-500">
        Per ogni notifica scegli i canali attivi (Email, WhatsApp, SMS) e personalizza il testo di ciascuno.
        Se il testo WhatsApp o SMS è vuoto, viene usato il testo dell'email. Gli SMS usano il wallet prepagato.
      </p>
      <div className="flex gap-1.5 flex-wrap">
        {CATS.map(([k, l]) => (
          <button key={k} onClick={() => { setCat(k); setSel(null); setPreview(null); }} data-testid={`tpl-cat-${k}`}
            className={`h-9 px-4 rounded-md text-xs font-bold border ${cat === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100 self-start" data-testid="tpl-list">
          {templates.filter((t) => t.categoria === cat).map((t) => (
            <button key={t.key} onClick={() => open(t)} data-testid={`tpl-item-${t.key}`}
              className={`w-full text-left px-4 py-3 flex items-center justify-between gap-2 hover:bg-zinc-50 ${sel === t.key ? "bg-zinc-50" : ""}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-900">{t.label}</div>
                <div className="flex gap-1 mt-1">
                  {CHANNELS.filter(([c]) => t.channels?.[c]).map(([c, l]) => (
                    <span key={c} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">{l}</span>
                  ))}
                </div>
              </div>
              {t.customized && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 shrink-0">PERSONALIZZATO</span>
              )}
            </button>
          ))}
        </div>
        {current ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4" data-testid="tpl-editor">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-900">{current.label}</h3>
              {!readOnly && (
                <button onClick={reset} className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1" data-testid="tpl-reset">
                  <ArrowCounterClockwise size={13} /> Ripristina predefinito
                </button>
              )}
            </div>

            <div className="flex gap-1.5" data-testid="tpl-channel-tabs">
              {CHANNELS.map(([c, l]) => (
                <div key={c} className="flex items-center gap-0">
                  <button onClick={() => setChTab(c)} data-testid={`tpl-tab-${c}`}
                    className={`h-9 px-3 rounded-l-md text-xs font-bold border ${chTab === c
                      ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
                    {l}
                  </button>
                  <button onClick={() => toggleCh(c)} data-testid={`tpl-toggle-${c}`}
                    title={form.channels[c] ? `${l} attivo — clicca per disattivare` : `${l} disattivo — clicca per attivare`}
                    className={`h-9 px-2 rounded-r-md border-y border-r text-[10px] font-bold ${form.channels[c]
                      ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-zinc-100 text-zinc-400 border-zinc-300"}`}>
                    {form.channels[c] ? "ON" : "OFF"}
                  </button>
                </div>
              ))}
            </div>

            {chTab === "email" && (
              <>
                <div>
                  <label className={labelCls}>Oggetto email</label>
                  <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className={inputCls} data-testid="tpl-subject" />
                </div>
                {current.layout && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tpl-layout-fields">
                    <div>
                      <label className={labelCls}>Titolo grande</label>
                      <input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                        className={inputCls} data-testid="tpl-titolo" />
                    </div>
                    <div>
                      <label className={labelCls}>Sottotitolo</label>
                      <input value={form.sottotitolo} onChange={(e) => setForm({ ...form, sottotitolo: e.target.value })}
                        className={inputCls} data-testid="tpl-sottotitolo" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Testo del pulsante (vuoto = nessun pulsante)</label>
                      <input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                        className={inputCls} data-testid="tpl-cta" placeholder="Gestisci Prenotazione" />
                    </div>
                  </div>
                )}
                <div>
                  <label className={labelCls}>Corpo email</label>
                  <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                    rows={4} ref={textareaRef} className={`${inputCls} h-auto py-2`} data-testid="tpl-body" />
                </div>
              </>
            )}
            {chTab === "whatsapp" && (
              <>
                {current.wa_meta?.status && (
                  <div className={`text-[11px] font-semibold px-3 py-2 rounded-md ${
                    current.wa_meta.status === "APPROVED" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                    current.wa_meta.status === "PENDING" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                    "bg-rose-50 text-rose-700 border border-rose-200"}`}
                    data-testid="tpl-wa-meta-status">
                    {current.wa_meta.status === "APPROVED" && (current.wa_meta.manual
                      ? `Template Meta agganciato: ${current.wa_meta.name} — il messaggio arriva sempre, anche fuori dalla finestra 24h`
                      : "Approvato da Meta — il messaggio arriva sempre, anche fuori dalla finestra 24h")}
                    {current.wa_meta.status === "PENDING" && "In revisione da Meta — finché non è approvato, il messaggio arriva solo se il cliente ti ha scritto nelle ultime 24h"}
                    {current.wa_meta.status === "REJECTED" && `Rifiutato da Meta${current.wa_meta.rejected_reason ? `: ${current.wa_meta.rejected_reason}` : ""} — modifica il testo e riprova`}
                    {current.wa_meta.status === "errore" && `Invio a Meta non riuscito: ${current.wa_meta.error || "errore"}`}
                  </div>
                )}
                <div>
                  <label className={labelCls}>Testo WhatsApp (vuoto = uguale al corpo email)</label>
                  <textarea value={form.wa_body} onChange={(e) => setForm({ ...form, wa_body: e.target.value })}
                    rows={5} ref={textareaRef} className={`${inputCls} h-auto py-2`} data-testid="tpl-wa-body"
                    placeholder="Testo breve e diretto per WhatsApp..." />
                  {!readOnly && (
                    <p className="text-[10px] text-zinc-400 mt-1">
                      Al salvataggio il testo viene inviato automaticamente a Meta per l'approvazione: una volta approvato, il messaggio arriva sempre. Fino ad allora vale la regola della finestra 24h.
                    </p>
                  )}
                </div>
                {!readOnly && (
                  <div>
                    <label className={labelCls}>Oppure aggancia un template Meta già approvato</label>
                    <select value={waTplSel} onChange={(e) => setWaTplSel(e.target.value)}
                      className={inputCls} data-testid="tpl-wa-template-select">
                      <option value="">— Automatico (usa il testo sopra, inviato a Meta) —</option>
                      {waApproved.map((t) => (
                        <option key={t.id || t.name} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                    {waTplSel && (
                      <p className="text-[10px] text-emerald-700 mt-1" data-testid="tpl-wa-template-note">
                        All'invio verrà usato il template "{waTplSel}": le variabili vengono mappate automaticamente.
                      </p>
                    )}
                    {waApproved.length === 0 && (
                      <p className="text-[10px] text-zinc-400 mt-1">
                        Nessun template approvato trovato. Sincronizzali da Marketing → Template WhatsApp.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            {chTab === "sms" && (
              <div>
                <label className={labelCls}>Testo SMS (vuoto = uguale al corpo email)</label>
                <textarea value={form.sms_body} onChange={(e) => setForm({ ...form, sms_body: e.target.value })}
                  rows={3} maxLength={480} ref={textareaRef} className={`${inputCls} h-auto py-2`} data-testid="tpl-sms-body"
                  placeholder="Testo breve per SMS (max 480 caratteri)..." />
                <p className="text-[10px] text-zinc-400 mt-1">{(form.sms_body || "").length}/480 caratteri · Gli SMS consumano il saldo del wallet</p>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5">
              {current.variables.map((v) => (
                <button key={v} type="button" onClick={() => insertVar(v)}
                  className="text-[10px] font-mono font-bold px-2 py-1 rounded bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  data-testid={`tpl-var-${v}`}>{`{{${v}}}`}</button>
              ))}
            </div>

            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid="tpl-preview">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                  Anteprima {CHANNELS.find(([c]) => c === chTab)?.[1]}
                </div>
                <button onClick={() => refreshPreview(sel, form)} disabled={previewLoading}
                  className="text-[10px] font-bold text-sky-700 hover:text-sky-900 flex items-center gap-1 disabled:opacity-50"
                  data-testid="tpl-refresh-preview">
                  <Eye size={12} weight="bold" /> {previewLoading ? "Aggiorno..." : "Aggiorna anteprima"}
                </button>
              </div>
              {chTab === "email" ? (
                preview?.html ? (
                  <iframe title="Anteprima email" srcDoc={preview.html} data-testid="tpl-preview-frame"
                    className="w-full h-[420px] bg-white rounded border border-zinc-200" sandbox="" />
                ) : (
                  <div className="text-xs text-zinc-400 py-6 text-center">
                    {previewLoading ? "Generazione anteprima..." : "Anteprima non disponibile"}
                  </div>
                )
              ) : (chTab === "whatsapp" ? preview?.wa : preview?.sms) ? (
                <div className="rounded-lg p-4 border border-zinc-200"
                  style={{ backgroundColor: chTab === "whatsapp" ? "#ece5dd" : "#f1f5f9" }}
                  data-testid={`tpl-preview-${chTab}`}>
                  <div className="max-w-[85%] bg-white rounded-lg rounded-tl-none px-3 py-2 shadow-sm">
                    <p className="text-sm text-zinc-900 whitespace-pre-line">
                      {chTab === "whatsapp" ? preview.wa : preview.sms}
                    </p>
                    <div className="text-right text-[10px] text-zinc-400 mt-1">20:00 {chTab === "whatsapp" ? "✓✓" : ""}</div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-zinc-400 py-6 text-center">
                  {previewLoading ? "Generazione anteprima..." : "Anteprima non disponibile"}
                </div>
              )}
            </div>
            {readOnly ? <ReadOnlyNote /> : (
              <button onClick={save} disabled={saving} className={btnPrimary} data-testid="tpl-save">Salva template</button>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-zinc-300 rounded-lg flex items-center justify-center text-sm text-zinc-400 p-8" data-testid="tpl-empty">
            Seleziona un template dalla lista
          </div>
        )}
      </div>
    </div>
  );
}
